import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

/** Normalize field from API to frontend shape. */
function normalizeField(f) {
  if (!f) return f;
  return {
    ...f,
    id: f.id ?? f.Id ?? f.LetterTemplateFieldId,
    companyId: f.companyId ?? f.CompanyId,
    fieldKey: f.fieldKey ?? f.FieldKey ?? "",
    fieldLabel: f.fieldLabel ?? f.FieldLabel ?? f.field_key ?? f.field_label ?? "",
    fieldType: (f.fieldType ?? f.FieldType ?? f.field_type ?? "text").toLowerCase(),
    displayOrder: f.displayOrder ?? f.DisplayOrder ?? f.display_order ?? 0,
  };
}

const initialState = {
  list: [],
  loading: false,
  error: null,
};

// GET /letterTemplate/LetterTemplateFieldGetByCompanyId
export const getFieldsByCompanyId = createAsyncThunk(
  "letterTemplateField/getFieldsByCompanyId",
  async (_arg, { rejectWithValue }) => {
    try {
      const { data } = await axios.get("letterTemplate/LetterTemplateFieldGetByCompanyId");
      let raw =
        data?.list ??
        data?.data ??
        data?.letterTemplateFields ??
        data?.LetterTemplateFields ??
        data?.fields ??
        data?.Fields ??
        (Array.isArray(data) ? data : []);
      if (!Array.isArray(raw)) raw = raw && (raw.Id != null || raw.id != null) ? [raw] : [];
      const list = Array.isArray(raw) ? raw : [];
      return list.map(normalizeField).sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to load fields";
      toast.error(msg);
      return rejectWithValue(error.response?.data || { message: msg });
    }
  }
);

// POST /letterTemplate/LetterTemplateFieldSetup
// Body: CompanyId, FieldKey, FieldLabel, FieldType, DisplayOrder (optional: Id for update)
export const saveField = createAsyncThunk(
  "letterTemplateField/saveField",
  async (payload, { rejectWithValue }) => {
    try {
      const body = {
        Id: payload.id ?? payload.Id ?? 0,
        CompanyId: payload.companyId ?? payload.CompanyId ?? 0,
        FieldKey: payload.fieldKey ?? payload.FieldKey ?? "",
        FieldLabel: payload.fieldLabel ?? payload.FieldLabel ?? "",
        FieldType: (payload.fieldType ?? payload.FieldType ?? "text").toLowerCase(),
        DisplayOrder: payload.displayOrder ?? payload.DisplayOrder ?? 0,
      };
      const { data } = await axios.post("letterTemplate/LetterTemplateFieldSetup", body);
      const saved = data?.data ?? data ?? body;
      toast.success(payload.id || payload.Id ? "Field updated" : "Field saved");
      return normalizeField(saved);
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to save field";
      toast.error(msg);
      return rejectWithValue(error.response?.data || { message: msg });
    }
  }
);

const letterTemplateFieldSlice = createSlice({
  name: "letterTemplateField",
  initialState,
  reducers: {
    setError(state, { payload }) {
      state.error = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFieldsByCompanyId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFieldsByCompanyId.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = Array.isArray(payload) ? payload : [];
        state.error = null;
      })
      .addCase(getFieldsByCompanyId.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload?.message || "Failed to load fields";
      })
      .addCase(saveField.fulfilled, (state, { payload }) => {
        if (!payload) return;
        const normalized = normalizeField(payload);
        const id = normalized.id ?? payload?.Id;
        const idx = (state.list || []).findIndex(
          (t) => (t.id ?? t.Id) === id || (t.fieldKey ?? t.FieldKey) === (normalized.fieldKey ?? payload?.FieldKey)
        );
        if (idx >= 0) {
          state.list = [...state.list];
          state.list[idx] = { ...state.list[idx], ...normalized };
        } else {
          state.list = [...(state.list || []), normalized].sort(
            (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
          );
        }
      });
  },
});

export const { setError } = letterTemplateFieldSlice.actions;
export default letterTemplateFieldSlice.reducer;
